import type { Dispatch, SetStateAction } from "react";
import { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import type { LessonAiDraft } from "../../auth/api-client";
import { syncMainPyInStarterFiles } from "../course-template-helpers";
import { LessonCodeEditor } from "./LessonCodeEditor";

export interface LessonCodeSectionProps {
  readonly draft: LessonAiDraft;
  readonly setDraft: Dispatch<SetStateAction<LessonAiDraft>>;
}

export function LessonCodeSection({ draft, setDraft }: LessonCodeSectionProps) {
  const isAdvanced = (draft.workspaceMode ?? "simple") === "advanced";

  const extraStarterFiles = useMemo(
    () => (draft.starterFiles ?? []).filter((f) => f.path !== "main.py"),
    [draft.starterFiles]
  );

  return (
    <div className="lesson-ws-section">
      <p className="muted small lesson-ws-section-lede">
        The code students see when they open the lesson. Keep starter scaffolding light so there is real
        work to do.
      </p>

      <label className="field">
        <span className="field-label">Starter main.py</span>
        <LessonCodeEditor
          value={draft.mainPy}
          onChange={(v) => setDraft((d) => ({ ...d, mainPy: v }))}
          ariaLabel="Starter main.py"
          minHeight="16rem"
        />
      </label>

      {isAdvanced ? (
        <div className="lesson-ws-helper-files">
          <div className="lesson-ws-subhead">
            <h3 className="lesson-ws-subhead-title">Helper files</h3>
            <p className="muted small">
              Students edit main.py above plus these extra files in the advanced workspace.
            </p>
          </div>

          {extraStarterFiles.map((file, i) => {
            const fileIndex = (draft.starterFiles ?? []).findIndex((f) => f.path === file.path);
            return (
              <fieldset key={file.path || i} className="teacher-step-fieldset">
                <legend className="fineprint">{file.path || `File ${i + 1}`}</legend>
                <label className="field">
                  <span className="field-label">File name</span>
                  <input
                    className="input"
                    value={file.path}
                    onChange={(ev) => {
                      const path = ev.target.value;
                      setDraft((d) => ({
                        ...d,
                        starterFiles: (d.starterFiles ?? []).map((f, idx) =>
                          idx === fileIndex ? { ...f, path } : f
                        ),
                      }));
                    }}
                    placeholder="helpers.py"
                  />
                </label>
                <label className="field">
                  <span className="field-label">Contents</span>
                  <LessonCodeEditor
                    value={file.content}
                    onChange={(v) =>
                      setDraft((d) => ({
                        ...d,
                        starterFiles: (d.starterFiles ?? []).map((f, idx) =>
                          idx === fileIndex ? { ...f, content: v } : f
                        ),
                      }))
                    }
                    ariaLabel={`Contents of ${file.path || "helper file"}`}
                    minHeight="9rem"
                  />
                </label>
                <button
                  type="button"
                  className="btn secondary danger tight"
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      starterFiles: (d.starterFiles ?? []).filter((f) => f.path !== file.path),
                    }))
                  }
                >
                  <FontAwesomeIcon icon={faTrash} /> Remove file
                </button>
              </fieldset>
            );
          })}

          <button
            type="button"
            className="btn secondary"
            onClick={() =>
              setDraft((d) => ({
                ...d,
                starterFiles: syncMainPyInStarterFiles(
                  [...(d.starterFiles ?? []), { path: "helpers.py", content: "# Helper functions\n" }],
                  d.mainPy
                ),
              }))
            }
          >
            <FontAwesomeIcon icon={faPlus} /> Add helper file
          </button>
        </div>
      ) : null}
    </div>
  );
}
